import _ from 'lodash';
import { Reducer } from 'redux';
import ContentConstants from '../contentConstants';
import {
	DistributionFileUploadType,
	DistributionType,
	DistributionUploadStatus,
	ExternalApplicationType,
	IAction,
	IColumn,
	IDistributionFile,
	IFileUploadResponse,
	IValidationErrorCollection,
} from '../models';
import { AccessCodeType } from '../models/AccessCodeType';
import * as DistributionAPI from '../services/DistributionAPI';
import { FileDownloadHelper } from '../utilities';
import { IApplicationState } from './';
import {
	IDistributionAddFailed,
	IDistributionAddRequested,
	IDistributionAddSucceeded,
	IDistributionAppendFailed,
	IDistributionAppendRequested,
	IDistributionAppendSucceeded,
	IDistributionArchiveFailed,
	IDistributionArchiveRequested,
	IDistributionArchiveSucceeded,
	IDistributionClearValidation,
	IDistributionDeleteFailed,
	IDistributionDeleteRequested,
	IDistributionDeleteSucceeded,
	IDistributionDownloadListFailed,
	IDistributionDownloadListRequested,
	IDistributionDownloadListSucceeded,
	IDistributionDownloadTemplateFailed,
	IDistributionDownloadTemplateRequested,
	IDistributionDownloadTemplateSucceeded,
	IDistributionInitState,
	IDistributionListValidation,
	IDistributionOptionDataTransferValidateFileFailed,
	IDistributionOptionDataTransferValidateFileRequested,
	IDistributionOptionDataTransferValidateFileSucceeded,
	IDistributionRemoveFromList,
	IDistributionsGetFailed,
	IDistributionsGetRequested,
	IDistributionsGetSucceeded,
	IDistributionValidateFileFailed,
	IDistributionValidateFileRequested,
	IDistributionValidateFileSucceeded,
} from './Actions';
import { ActionTypes } from './ActionTypes';
import { SsoAttribute } from '../models/SsoAttribute';

export const defaultColumns: IColumn[] = [
	{ name: 'fileName', displayText: 'NAME', displayOrder: 1, disabled: true, checked: true, isSortable: false, sortAscByDefault: false },
	{
		name: 'employeesCount',
		displayText: 'NUMBER OF EMPLOYEES',
		displayOrder: 2,
		disabled: false,
		checked: true,
		isSortable: false,
		sortAscByDefault: false,
	},
	{ name: 'Status', displayText: 'STATUS', displayOrder: 3, disabled: false, checked: true, isSortable: false, sortAscByDefault: false },
	{ name: 'actions', displayText: 'ACTIONS', disabled: false, displayOrder: 4, checked: true },
];

export interface IDistributionFileState {
	distributions: IDistributionFile[];
	distribution?: IDistributionFile;
	file?: any;
	isDistributionLoading: boolean;
	isDownloading?: boolean;
	isUploading: boolean;
	isTransferDataFileValidating: boolean;
	uploadComplete?: boolean;
	distributionColumns: IColumn[];
	distributionValidationErrors?: IValidationErrorCollection;
	distributionValidationWarnings?: IValidationErrorCollection;
	distributionListValidationErrors?: IValidationErrorCollection;
	fileUploadType?: DistributionFileUploadType;
	selectedDistribution?: IDistributionFile;
}

export const defaultState: IDistributionFileState = {
	file: undefined,
	isDistributionLoading: false,
	isDownloading: false,
	isUploading: false,
	isTransferDataFileValidating: false,
	distributions: [],
	distribution: undefined,
	distributionColumns: [...defaultColumns],
	distributionValidationErrors: undefined,
	fileUploadType: undefined,
	selectedDistribution: undefined,
};

type KnownActions =
	| IDistributionsGetRequested
	| IDistributionsGetFailed
	| IDistributionsGetSucceeded
	| IDistributionAddRequested
	| IDistributionAddFailed
	| IDistributionAddSucceeded
	| IDistributionAppendRequested
	| IDistributionAppendFailed
	| IDistributionAppendSucceeded
	| IDistributionDeleteRequested
	| IDistributionDeleteFailed
	| IDistributionDeleteSucceeded
	| IDistributionArchiveRequested
	| IDistributionArchiveFailed
	| IDistributionArchiveSucceeded
	| IDistributionRemoveFromList
	| IDistributionDownloadListRequested
	| IDistributionDownloadListSucceeded
	| IDistributionDownloadListFailed
	| IDistributionDownloadTemplateRequested
	| IDistributionDownloadTemplateSucceeded
	| IDistributionDownloadTemplateFailed
	| IDistributionInitState
	| IDistributionListValidation
	| IDistributionClearValidation
	| IDistributionValidateFileRequested
	| IDistributionValidateFileSucceeded
	| IDistributionValidateFileFailed
	| IDistributionOptionDataTransferValidateFileRequested
	| IDistributionOptionDataTransferValidateFileSucceeded
	| IDistributionOptionDataTransferValidateFileFailed;

export const ActionCreators = {
	GetDistributions: (surveyId: number, audienceId: number) => (dispatch: any) => {
		dispatch({ type: ActionTypes.DISTRIBUTIONS_GET_REQUESTED });

		return DistributionAPI.getDistributions(surveyId, audienceId)
			.then((distributions: IDistributionFile[]) => {
				dispatch({ type: ActionTypes.DISTRIBUTIONS_GET_SUCCEEDED, payload: { distributions } });
			})
			.catch((ex) => {
				dispatch({ type: ActionTypes.DISTRIBUTIONS_GET_FAILED });
			});
	},
	ValidateFile:
		(
			surveyId: number,
			audienceId: number,
			fileUploadType: DistributionFileUploadType,
			file: any,
			distributionType: number,
			codeType?: AccessCodeType,
			ssoAttribute?: SsoAttribute,
		) =>
		(dispatch: any) => {
			dispatch({ type: ActionTypes.DISTRIBUTION_VALIDATE_FILE_REQUESTED, payload: { fileUploadType } });
			dispatch({ type: ActionTypes.TOAST_PROCESSING_NOTIFICATION, payload: ContentConstants.TOAST_VALIDATING_PARTICIPANTS_TEXT });

			return DistributionAPI.ValidateFile(surveyId, audienceId, fileUploadType, file, distributionType, codeType, ssoAttribute)
				.then((newDistribution: IDistributionFile) => {
					dispatch({ type: ActionTypes.DISTRIBUTION_VALIDATE_FILE_SUCCEEDED, payload: { distribution: newDistribution, file } });
					dispatch({ type: ActionTypes.TOAST_SUCCESS_NOTIFICATION, payload: ContentConstants.TOAST_VALIDATED_PARTICIPANTS_TEXT });
				})
				.catch((ex) => {
					let validationErrors;
					if (ex.response && ex.response.data && ex.response.data.validationErrors) {
						validationErrors = ex.response.data.validationErrors.validations;
					}

					if (!validationErrors && ex.response.data) {
						dispatch({ type: ActionTypes.TOAST_DISMISS_ALL });
						dispatch({ type: ActionTypes.TOAST_WARNING_NOTIFICATION, payload: ex.response.data.Detail });
					} else {
						dispatch({ type: ActionTypes.DISTRIBUTION_VALIDATE_FILE_FAILED, payload: { distributionValidationErrors: validationErrors } });

						dispatch({ type: ActionTypes.TOAST_DISMISS_ALL });
						dispatch({ type: ActionTypes.TOAST_WARNING_NOTIFICATION, payload: ContentConstants.TOAST_FILE_UPLOAD_FAILED_MESSAGE });
					}
				});
		},
	AddDistribution:
		(surveyId: number, audienceId: number, file: any, deleteBeforeUpload: boolean, distributionType: number, ssoAttribute?: SsoAttribute) =>
		(dispatch: any) => {
			return new Promise<void>((resolve, reject) => {
				dispatch({ type: ActionTypes.DISTRIBUTION_ADD_REQUESTED, payload: { fileUploadType: DistributionFileUploadType.New } });
				dispatch({ type: ActionTypes.TOAST_PROCESSING_NOTIFICATION, payload: ContentConstants.TOAST_UPLOADING_PARTICIPANTS_TEXT });

				return DistributionAPI.AddDistribution(surveyId, audienceId, file, deleteBeforeUpload, distributionType, ssoAttribute)
					.then((response: IFileUploadResponse) => {
						dispatch({
							type: ActionTypes.DISTRIBUTION_ADD_SUCCEEDED,
							payload: {
								distribution: response.distribution,
								file,
								contactsCount: response.contactsCount,
								validationWarnings: response.validationWarnings,
								fileName: response.fileName,
							},
						});
						dispatch({ type: ActionTypes.TOAST_SUCCESS_NOTIFICATION, payload: ContentConstants.TOAST_UPLOADED_PARTICIPANTS_TEXT });
						resolve();
					})
					.catch((ex) => {
						let validationErrors;
						if (ex.response && ex.response.data && ex.response.data.validationErrors) {
							validationErrors = ex.response.data.validationErrors.validations;
						}
						dispatch({ type: ActionTypes.DISTRIBUTION_ADD_FAILED, payload: { distributionValidationErrors: validationErrors } });
						dispatch({ type: ActionTypes.TOAST_DISMISS_ALL });
						reject();
					});
			});
		},
	AppendDistribution: (surveyId: number, audienceId: number, file: any, distributionType: number) => (dispatch: any) => {
		dispatch({ type: ActionTypes.DISTRIBUTION_APPEND_REQUESTED, payload: { fileUploadType: DistributionFileUploadType.Append } });
		dispatch({ type: ActionTypes.TOAST_PROCESSING_NOTIFICATION, payload: ContentConstants.TOAST_UPLOADING_PARTICIPANTS_TEXT });

		return DistributionAPI.AppendDistribution(surveyId, audienceId, file, distributionType)
			.then((response: IFileUploadResponse) => {
				dispatch({
					type: ActionTypes.DISTRIBUTION_APPEND_SUCCEEDED,
					payload: {
						distribution: response.distribution,
						file,
						contactsCount: response.contactsCount,
						validationWarnings: response.validationWarnings,
						fileName: response.fileName,
					},
				});
				dispatch({ type: ActionTypes.TOAST_SUCCESS_NOTIFICATION, payload: ContentConstants.TOAST_UPLOADED_PARTICIPANTS_TEXT });
			})
			.catch((ex) => {
				let validationErrors;
				if (ex.response && ex.response.data && ex.response.data.validationErrors) {
					validationErrors = ex.response.data.validationErrors.validations;
				}
				dispatch({ type: ActionTypes.DISTRIBUTION_APPEND_FAILED, payload: { distributionValidationErrors: validationErrors } });
				dispatch({ type: ActionTypes.TOAST_DISMISS_ALL });
			});
	},
	ClearDistributionValidation: () => (dispatch: any) => {
		dispatch({ type: ActionTypes.DISTRIBUTION_CLEAR_VALIDATION });
	},
	RemoveDistributionFromList: () => (dispatch: any) => {
		dispatch({ type: ActionTypes.DISTRIBUTION_REMOVE_FROM_LIST });
	},
	DeleteDistribution: (surveyId: number, audienceId: number) => (dispatch: any) => {
		dispatch({ type: ActionTypes.DISTRIBUTION_DELETE_REQUESTED });

		return DistributionAPI.deleteDistribution(surveyId, audienceId, true)
			.then(() => {
				dispatch({ type: ActionTypes.DISTRIBUTION_DELETE_SUCCEEDED, payload: {} });
			})
			.catch(() => {
				dispatch({ type: ActionTypes.DISTRIBUTION_DELETE_FAILED });
			});
	},
	ArchiveDistributionsOfAudience: (surveyId: number, audienceId: number) => (dispatch: any) => {
		dispatch({ type: ActionTypes.DISTRIBUTION_ARCHIVE_REQUESTED });

		return DistributionAPI.archiveDistributionsOfAudience(surveyId, audienceId)
			.then(() => {
				dispatch({ type: ActionTypes.DISTRIBUTION_ARCHIVE_SUCCEEDED });
			})
			.catch(() => {
				dispatch({ type: ActionTypes.DISTRIBUTION_ARCHIVE_FAILED });
			});
	},
	DownloadDistributionList: (surveyId: number, audienceId: number) => (dispatch: any) => {
		dispatch({ type: ActionTypes.DISTRIBUTION_DOWNLOAD_LIST_REQUESTED });
		dispatch({ type: ActionTypes.TOAST_PROCESSING_GENERIC_NOTIFICATION });
		return DistributionAPI.downloadDistributionList(surveyId, audienceId)
			.then((response) => {
				FileDownloadHelper.download(response, 'DistributionList.xlsx');
				dispatch({ type: ActionTypes.DISTRIBUTION_DOWNLOAD_LIST_SUCCEEDED });
				dispatch({ type: ActionTypes.TOAST_DISMISS_ALL });
			})
			.catch((ex) => {
				dispatch({ type: ActionTypes.DISTRIBUTION_DOWNLOAD_LIST_FAILED });
				dispatch({ type: ActionTypes.TOAST_WARNING_GENERIC_NOTIFICATION });
			});
	},
	DownloadDistributionTemplate:
		(distributionType: DistributionType, externalApplicationType?: ExternalApplicationType) =>
		(dispatch: any, getState: () => IApplicationState) => {
			const { isDownloading } = getState().distributionList;

			if (!isDownloading) {
				dispatch({ type: ActionTypes.DISTRIBUTION_DOWNLOAD_TEMPLATE_REQUESTED });

				return DistributionAPI.downloadDistributionTemplate(distributionType, externalApplicationType)
					.then((response) => {
						FileDownloadHelper.download(response, 'DistributionList.xlsx');
						dispatch({ type: ActionTypes.DISTRIBUTION_DOWNLOAD_TEMPLATE_SUCCEEDED });
					})
					.catch((ex) => {
						dispatch({ type: ActionTypes.DISTRIBUTION_DOWNLOAD_TEMPLATE_FAILED });
					});
			}
		},
	AddDistributionListValidationErrors: (validationError?: IValidationErrorCollection) => (dispatch: any) => {
		dispatch({ type: ActionTypes.DISTRIBUTION_LIST_VALIDATION, payload: { validationError } });
	},
};

export const reducer: Reducer<IDistributionFileState, KnownActions> = (
	state: IDistributionFileState = defaultState,
	action: KnownActions,
) => {
	switch (action.type) {
		case ActionTypes.DISTRIBUTION_INIT_STATE:
			return { ..._.cloneDeep(defaultState) };
		case ActionTypes.DISTRIBUTIONS_GET_REQUESTED:
			return { ...state, isDistributionLoading: true, distributionValidationWarnings: undefined };

		case ActionTypes.DISTRIBUTIONS_GET_SUCCEEDED: {
			const distributions = action.payload.distributions;
			let distribution: IDistributionFile | undefined;
			if (distributions) {
				distribution = _.find(distributions, (d: IDistributionFile) => {
					return d.distributionFileUploadTypeId === DistributionFileUploadType.New;
				});
				if (distribution) {
					const completedDistributions = _.map(distributions, (d: IDistributionFile) => {
						// Consider only the ones completed and old distributions with no status
						if (!d.distributionUploadStatusId || d.distributionUploadStatusId === DistributionUploadStatus.Completed) {
							return d;
						}
						return {};
					});
					if (completedDistributions) {
						distribution.contactsCount = _.sumBy(completedDistributions, 'contactsCount');
					}
				}
			}

			return {
				...state,
				distributions,
				distribution,
				distributionValidationWarnings:
					distribution && distribution.validationWarnings ? distribution.validationWarnings.validations : undefined,
				isDistributionLoading: false,
				file: undefined,
				selectedDistribution: undefined,
			};
		}
		case ActionTypes.DISTRIBUTIONS_GET_FAILED: {
			return {
				...state,
				isDistributionLoading: false,
			};
		}

		case ActionTypes.DISTRIBUTION_APPEND_REQUESTED:
		case ActionTypes.DISTRIBUTION_ADD_REQUESTED:
			return {
				...state,
				fileUploadType: action.payload.fileUploadType,
				isUploading: true,
				uploadComplete: false,
				distributionValidationErrors: undefined,
				distributionListValidationErrors: undefined,
				distributionValidationWarnings: undefined,
				file: undefined,
			};

		case ActionTypes.DISTRIBUTION_ADD_SUCCEEDED: {
			const returnedDistribution = action.payload.distribution;
			let distribution: any = state.distribution;
			if (state.fileUploadType === DistributionFileUploadType.New) {
				distribution = distribution || {};
			}
			if (distribution) {
				distribution = { ...distribution, action: IAction.None };
			}

			return {
				...state,
				distribution,
				file: action.payload.file,
				uploadComplete: true,
				isUploading: false,
				distributionValidationErrors: undefined,
				distributionValidationWarnings:
					returnedDistribution && returnedDistribution.validationWarnings && returnedDistribution.validationWarnings.validations,
				selectedDistribution: undefined,
			};
		}

		case ActionTypes.DISTRIBUTION_APPEND_FAILED:
		case ActionTypes.DISTRIBUTION_ADD_FAILED:
			return {
				...state,
				distributionValidationErrors: action.payload.distributionValidationErrors,
				file: action.payload.file,
				isUploading: false,
			};

		case ActionTypes.DISTRIBUTION_APPEND_SUCCEEDED: {
			const returnedDistribution = action.payload.distribution;
			let distribution: any = state.distribution;
			if (distribution) {
				distribution = { ...distribution, action: IAction.None };
			} else {
				distribution = returnedDistribution;
			}

			return {
				...state,
				distribution,
				file: action.payload.file,
				uploadComplete: true,
				isUploading: false,
				distributionValidationErrors: undefined,
				distributionValidationWarnings:
					returnedDistribution && returnedDistribution.validationWarnings && returnedDistribution.validationWarnings.validations,
				selectedDistribution: undefined,
			};
		}

		case ActionTypes.DISTRIBUTION_DELETE_REQUESTED:
			return { ...state, isSaving: true };
		case ActionTypes.DISTRIBUTION_DELETE_SUCCEEDED:
			return {
				...state,
				distributions: action.payload.distributions,
				isSaving: false,
			};
		case ActionTypes.DISTRIBUTION_DELETE_FAILED:
			return { ...state, isSaving: false };
		case ActionTypes.DISTRIBUTION_ARCHIVE_REQUESTED:
			return { ...state, isSaving: true };
		case ActionTypes.DISTRIBUTION_ARCHIVE_SUCCEEDED:
			return {
				...state,
				distributions: [],
				distribution: undefined,
				isSaving: false,
			};
		case ActionTypes.DISTRIBUTION_ARCHIVE_FAILED:
			return { ...state, isSaving: false };
		case ActionTypes.DISTRIBUTION_REMOVE_FROM_LIST: {
			const distribution: any = state.distribution;
			return {
				...state,
				file: undefined,
				fileUploadType: undefined,
				distribution: { ...distribution, action: IAction.Delete },
				distributionValidationWarnings: undefined,
				distributions: [],
				selectedDistribution: undefined,
			};
		}
		case ActionTypes.DISTRIBUTION_DOWNLOAD_LIST_REQUESTED:
			return { ...state, isDownloading: true };
		case ActionTypes.DISTRIBUTION_DOWNLOAD_LIST_SUCCEEDED:
		case ActionTypes.DISTRIBUTION_DOWNLOAD_LIST_FAILED: {
			return { ...state, isDownloading: false };
		}
		case ActionTypes.DISTRIBUTION_DOWNLOAD_TEMPLATE_REQUESTED:
			return { ...state, isDownloading: true, loading: false };
		case ActionTypes.DISTRIBUTION_DOWNLOAD_TEMPLATE_SUCCEEDED:
		case ActionTypes.DISTRIBUTION_DOWNLOAD_TEMPLATE_FAILED: {
			return {
				...state,
				loading: false,
				isDownloading: false,
			};
		}
		case ActionTypes.DISTRIBUTION_LIST_VALIDATION: {
			return { ...state, distributionListValidationErrors: action.payload.validationError };
		}
		case ActionTypes.DISTRIBUTION_CLEAR_VALIDATION: {
			let distribution: IDistributionFile | undefined = state.distribution;
			if (distribution) {
				distribution = { ...distribution, validationWarnings: undefined };
			}
			return {
				...state,
				distributionValidationErrors: undefined,
				distributionValidationWarnings: undefined,
				distribution,
			};
		}
		case ActionTypes.DISTRIBUTION_VALIDATE_FILE_REQUESTED: {
			return {
				...state,
				isUploading: true,
				fileUploadType: action.payload.fileUploadType,
				distributionValidationErrors: undefined,
				distributionValidationWarnings: undefined,
			};
		}
		case ActionTypes.DISTRIBUTION_VALIDATE_FILE_SUCCEEDED: {
			const newDistribution = action.payload.distribution;
			let distribution: any = state.distribution;
			if (distribution) {
				distribution = { ...distribution, action: IAction.None, distributionUploadStatusId: DistributionUploadStatus.NotStarted };
			}

			return {
				...state,
				distribution,
				file: action.payload.file,
				uploadComplete: true,
				isUploading: false,
				distributionValidationErrors: undefined,
				distributionListValidationErrors: undefined,
				distributionValidationWarnings:
					action.payload.distribution.validationWarnings && action.payload.distribution.validationWarnings.validations,
				selectedDistribution: newDistribution,
			};
		}
		case ActionTypes.DISTRIBUTION_VALIDATE_FILE_FAILED: {
			return { ...state, isUploading: false, distributionValidationErrors: action.payload.distributionValidationErrors };
		}

		// Data transfer validating
		case ActionTypes.DISTRIBUTION_OPTION_DATA_TRANSFER_VALIDATE_FILE_REQUESTED: {
			return { ...state, isTransferDataFileValidating: true };
		}
		case ActionTypes.DISTRIBUTION_OPTION_DATA_TRANSFER_VALIDATE_FILE_SUCCEEDED: {
			return { ...state, isTransferDataFileValidating: false };
		}
		case ActionTypes.DISTRIBUTION_OPTION_DATA_TRANSFER_VALIDATE_FILE_FAILED: {
			return { ...state, isTransferDataFileValidating: false };
		}
		default:
			return state;
	}
};
